import { Component, OnInit, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { PaginatedResponse, PaginationInfo } from 'src/app/shared/models';
import { AuctionHallService } from 'src/app/services/auction-hall/auction-hall.service';
import { AuctionHallModalService } from 'src/app/services/auction-hall-modal/auction-hall-modal.service';
import Swal from 'sweetalert2';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

declare var bootstrap: any;
declare var KTMenu: any;

@Component({
  selector: 'app-auction-halls',
  templateUrl: './auction-halls.component.html',
  styleUrls: ['./auction-halls.component.scss'],
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ height: '0px', opacity: 0 }),
        animate('300ms ease-in', style({ height: '*', opacity: 1 }))
      ]),
      transition(':leave', [
        animate('300ms ease-out', style({ height: '0px', opacity: 0 }))
      ])
    ])
  ]
})
export class AuctionHallsComponent extends DestructorComponent implements OnInit, AfterViewInit {

  // Data properties
  auctionHalls: any[] = [];
  paginationInfo: PaginationInfo = {
    currentPage: 1,
    perPage: 20,
    total: 0,
    lastPage: 1,
    firstPage: 1
  };

  // State properties
  isLoading: boolean = false;
  searchTerm: string = '';
  currentPage: number = 1;
  showFiltersForm = false;

  // Filter properties
  filters = {
    search: '',
    date_from: '',
    date_to: ''
  };

  // Search subject for debouncing
  private searchSubject = new Subject<string>();

  constructor(
    private _auctionHallService: AuctionHallService,
    private _auctionHallModalService: AuctionHallModalService,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.loadAuctionHalls();
    this.setupSearchDebounce();
  }

  private setupSearchDebounce(): void {
    this.subscriptions.push(
      this.searchSubject.pipe(
        debounceTime(500), // Wait 500ms after user stops typing
        distinctUntilChanged() // Only emit if value is different from previous
      ).subscribe(searchTerm => {
        this.filters.search = searchTerm;
        this.loadAuctionHalls(1, searchTerm, this.filters);
      })
    );
  }

  onSearchKeyup(): void {
    this.searchSubject.next(this.searchTerm);
  }

  ngAfterViewInit(): void {
    this.initializeMenus();
  }

  loadAuctionHalls(page: number = 1, search: string = '', filters: Record<string, any> = {}): void {
    this.isLoading = true;
    this.currentPage = page;

    delete filters.search;
    this.subscriptions.push(
      this._auctionHallService.GetData(page.toString(), search, filters).subscribe({
        next: (response: PaginatedResponse<any>) => {
          if (response.status && response.data) {
            this.auctionHalls = response.data.data || [];
            this.paginationInfo = response.data.pagination;
            this.currentPage = response.data.pagination.currentPage;
          } else {
            this.auctionHalls = [];
            this.resetPagination();
          }
          this.isLoading = false;
          this.cdr.detectChanges();
          this.refreshMenus();
        },
        error: (error) => {
          console.error('Error loading auction halls:', error);
          this.auctionHalls = [];
          this.resetPagination();
          this.isLoading = false;
          this.cdr.detectChanges();
          this.refreshMenus();
        }
      })
    );
  }

  onPageChange(page: number): void {
    this.loadAuctionHalls(page, this.searchTerm, this.filters);
  }

  toggleFilters(): void {
    this.showFiltersForm = !this.showFiltersForm;
  }

  applyFilters(): void {
    this.filters.search = this.searchTerm;
    this.loadAuctionHalls(1, this.searchTerm, this.filters);
  }

  clearFilters(): void {
    this.filters = {
      search: '',
      date_from: '',
      date_to: ''
    };
    this.searchTerm = '';
    this.loadAuctionHalls(1, '', {});
  }

  openAddAuctionHallModal(): void {
    this._auctionHallModalService.clearSelectedAuctionHall();
    const modalElement = document.getElementById('kt_modal_auction_hall_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  openEditAuctionHallModal(auctionHall: any): void {
    this._auctionHallModalService.setSelectedAuctionHall(auctionHall);
    const modalElement = document.getElementById('kt_modal_auction_hall_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  deleteAuctionHall(auctionHall: any): void {
    Swal.fire({
      title: 'Are you sure?',
      text: `You want to delete auction hall "${auctionHall.name || 'this auction hall'}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performDelete(auctionHall);
      }
    });
  }

  private performDelete(auctionHall: any): void {
    const auctionHallId = auctionHall._id || auctionHall.id;
    this.subscriptions.push(
      this._auctionHallService.DeleteDataById(auctionHallId).subscribe({
        next: (response: any) => {
          if (response.status) {
            Swal.fire({
              title: 'Deleted!',
              text: 'Auction hall has been deleted successfully.',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.loadAuctionHalls(this.currentPage, this.searchTerm, this.filters);
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to delete auction hall.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error: any) => {
          console.error('Error deleting auction hall:', error);
          Swal.fire({
            title: 'Error!',
            text: error.error?.message || 'An error occurred while deleting the auction hall.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }

  onAuctionHallAdded(): void {
    console.log('Auction hall added, refreshing list...');
    this.loadAuctionHalls(this.currentPage, this.searchTerm, this.filters);
  }

  onAuctionHallUpdated(): void {
    console.log('Auction hall updated, refreshing list...');
    this.loadAuctionHalls(this.currentPage, this.searchTerm, this.filters);
  }

  toggleActiveStatus(auctionHall: any): void {
    const newStatus = !auctionHall.active;
    const statusText = newStatus ? 'activate' : 'deactivate';

    Swal.fire({
      title: 'Are you sure?',
      text: `You want to ${statusText} auction hall "${auctionHall.name || 'this auction hall'}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: newStatus ? '#50cd89' : '#f1416c',
      cancelButtonColor: '#3085d6',
      confirmButtonText: `Yes, ${statusText} it!`,
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performToggleStatus(auctionHall, newStatus);
      }
    });
  }

  private performToggleStatus(auctionHall: any, newStatus: boolean): void {
    const auctionHallId = auctionHall._id || auctionHall.id;
    this.subscriptions.push(
      this._auctionHallService.UpdateData(auctionHallId, { active: newStatus }).subscribe({
        next: (response: any) => {
          if (response.status) {
            Swal.fire({
              title: 'Success!',
              text: `Auction hall has been ${newStatus ? 'activated' : 'deactivated'} successfully.`,
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.loadAuctionHalls(this.currentPage, this.searchTerm, this.filters);
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to update auction hall status.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error: any) => {
          console.error('Error updating auction hall status:', error);
          Swal.fire({
            title: 'Error!',
            text: error.error?.message || 'An error occurred while updating the auction hall status.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }

  private resetPagination(): void {
    this.paginationInfo = {
      currentPage: 1,
      perPage: 20,
      total: 0,
      lastPage: 1,
      firstPage: 1
    };
  }

  // Initialize KTMenu instances
  private initializeMenus(): void {
    setTimeout(() => {
      if (typeof KTMenu !== 'undefined') {
        KTMenu.createInstances();
      }
    }, 100);
  }

  // Refresh menus after data load
  private refreshMenus(): void {
    setTimeout(() => {
      if (typeof KTMenu !== 'undefined') {
        KTMenu.createInstances();
      }
    }, 50);
  }

  trackByFn(index: number, item: any): any {
    return item._id || item.id || index;
  }
}
